import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { parse, format, isValid } from "date-fns"
import { 
  PaymentOption, 
  DeliveryMode, 
  HybridSchedule, 
  BatchSchedule 
} from "@/types/program"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Validates and formats a date string using date-fns
 * 
 * @param input The date string to validate and format
 * @param outputFormat The format to output the date in (defaults to ISO)
 * @returns Object containing validation status and formatted date
 */
export const validateAndFormatDate = (
  input: string,
  outputFormat: string = "yyyy-MM-dd"
): { isValid: boolean; formattedDate: string } => {
  // Allow empty input
  if (!input?.trim()) {
    return { isValid: false, formattedDate: "" };
  }

  // Try to parse different common formats
  const dateFormats = [
    "MM/dd/yyyy", 
    "M/d/yyyy", 
    "MM-dd-yyyy", 
    "M-d-yyyy", 
    "yyyy-MM-dd",
    "yyyy/MM/dd"
  ];
  
  for (const dateFormat of dateFormats) {
    const parsedDate = parse(input, dateFormat, new Date());
    if (isValid(parsedDate)) {
      return { 
        isValid: true, 
        formattedDate: format(parsedDate, outputFormat) 
      };
    }
  }
  
  // Return the original input if not valid
  return { isValid: false, formattedDate: input };
};

/**
 * Formats a date string as the user types (MM/DD/YYYY)
 * 
 * @param input The raw input from the user
 * @returns Formatted date string
 */
export const formatDateAsUserTypes = (input: string): string => {
  // Strip any non-digit characters
  const digitsOnly = input.replace(/\D/g, "");
  
  // Format as MM/DD/YYYY
  if (digitsOnly.length <= 2) {
    return digitsOnly;
  } else if (digitsOnly.length <= 4) {
    return `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2)}`;
  } else {
    return `${digitsOnly.slice(0, 2)}/${digitsOnly.slice(2, 4)}/${digitsOnly.slice(4, 8)}`;
  }
};

// API Compatibility Utils
export const DELIVERY_MODE_MAP: Record<DeliveryMode, string> = {
  "hybrid": "Hybrid",
  "via-zoom": "Via-Zoom", 
  "on-campus": "On-Campus",
  "online": "Online"
};

// Legacy to new program type mapping
export const LEGACY_TO_NEW_PROGRAM_TYPE: Record<string, DeliveryMode> = {
  "onCampus": "on-campus",
  "viaZoom": "via-zoom",
  "hybrid": "hybrid",
  "online": "online"
};

// New to legacy program type mapping (for backward compatibility)
export const NEW_TO_LEGACY_PROGRAM_TYPE: Record<DeliveryMode, string> = {
  "on-campus": "onCampus",
  "via-zoom": "viaZoom", 
  "hybrid": "hybrid",
  "online": "online"
};

/**
 * Transform payment options from form format (array paymentTags) to API format (string paymentTags)
 */
export const transformPaymentOptionsToAPI = (paymentOptions: PaymentOption[]): PaymentOption[] => {
  return paymentOptions.map(option => ({
    ...option,
    paymentTags: Array.isArray(option.paymentTags) 
      ? option.paymentTags.join(', ') 
      : option.paymentTags
  }));
};

/**
 * Transform payment options from API format (string paymentTags) to form format (array paymentTags)
 */
export const transformPaymentOptionsFromAPI = (paymentOptions: PaymentOption[]): PaymentOption[] => {
  return paymentOptions.map(option => ({
    ...option,
    paymentTags: typeof option.paymentTags === 'string' 
      ? option.paymentTags
      : Array.isArray(option.paymentTags) 
        ? (option.paymentTags as string[]).join(', ')
        : option.paymentTags
  }));
};

/**
 * Create schedule object based on program type
 */
export const createScheduleForProgramType = (
  programType: DeliveryMode, 
  programSchedule: any
): HybridSchedule | BatchSchedule => {
  if (programType === "hybrid" || programType === "online") {
    return {
      startMonth: programSchedule?.hybrid?.startMonth || [],
      startDate: programSchedule?.hybrid?.startDate || []
    } as HybridSchedule;
  } else {
    const scheduleKey = programType === "on-campus" ? "on-campus" : "via-zoom";
    return {
      batches: programSchedule?.[scheduleKey]?.batches || []
    } as BatchSchedule;
  }
};

/**
 * Transform API response to form data format
 */
export const transformAPIDataToForm = (apiData: any): any => {
  if (!apiData.data?.program) {
    return apiData;
  }

  const program = apiData.data.program;
  const modules = apiData.data.modules || [];

  return {
    title: program.title,
    description: program.description,
    category: program.category,
    status: program.status,
    price: program.price,
    duration: program.duration,
    level: program.level,
    language: program.language,
    instructor: program.instructor,
    thumbnail: program.thumbnail,
    banner: program.banner,
    videoUrl: program.videoUrl,
    slug: program.slug,
    
    // Transform modules to form format
    modules: modules.map((module: any) => ({
      label: module.label,
      price: module.price,
      isActive: module.isActive,
      order: module.order
    })),
    
    payAsYouGo: program.payAsYouGo,
    modulePricingEnabled: program.modulePricingEnabled,
    
    features: program.features || [],
    learningOutcomes: program.learningOutcomes || [],
    prerequisites: program.prerequisites || [],
    faqs: program.faqs || [],
    clinicalProcedures: program.clinicalProcedures || [],
    nationalCertifications: program.nationalCertifications,
    
    isDraft: program.isDraft,
    draftNotes: program.draftNotes
  };
};

/**
 * Transform form data to API format
 */
export function transformFormDataToAPI(formData: any): any {
  const cleanedFormData = { ...formData };
  
  // Clean up categoryId - handle both string and object formats
  if (typeof cleanedFormData.categoryId === 'object' && cleanedFormData.categoryId?._id) {
    cleanedFormData.categoryId = cleanedFormData.categoryId._id;
  } else if (typeof cleanedFormData.categoryId === 'string') {
    // Keep as is
  } else {
    // If categoryId is undefined or null, set to empty string
    cleanedFormData.categoryId = cleanedFormData.categoryId || '';
  }
  
  // Handle modules - preserve the enhanced module structure with nested items
  let modules = cleanedFormData.modules || [];
  
  // If we have moduleConfiguration with enhanced modules, use those
  if (cleanedFormData.moduleConfiguration?.modules && cleanedFormData.moduleConfiguration.modules.length > 0) {
    // Convert enhanced modules to the format expected by the API with nested items
    modules = cleanedFormData.moduleConfiguration.modules.map((enhancedModule: any, index: number) => ({
      label: enhancedModule.name || enhancedModule.label || `Module ${index + 1}`,
      isActive: enhancedModule.isActive !== false,
      order: enhancedModule.order || index + 1,
      items: enhancedModule.items || [] // Preserve nested items structure
    }));
  } else if (modules.length > 0) {
    // Ensure existing modules have the proper structure with items
    modules = modules.map((module: any, index: number) => ({
      label: module.label || `Module ${index + 1}`,
      isActive: module.isActive !== false,
      order: module.order || index + 1,
      items: module.items || [] // Ensure items array exists
    }));
  }

  // Create shared data structure (same across all program types)
  const sharedData = {
    title: cleanedFormData.title,
    subtitle: cleanedFormData.subtitle,
    videoUrl: cleanedFormData.videoUrl,
    language: cleanedFormData.language,
    status: cleanedFormData.status,
    tagline: cleanedFormData.tagline,
    socialMedia: cleanedFormData.socialMedia || {
      facebook: null,
      twitter: null,
      instagram: null,
      linkedin: null,
      youtube: null,
      website: null
    },
    about: cleanedFormData.about,
    breadcrumb: cleanedFormData.breadcrumb || [],
  };

  // Create type-specific data structure (different per program type)
  const getTypeSpecificData = (perType: any) => ({
    modules: perType?.modules || modules,
    keyFeatures: perType?.keyFeatures || cleanedFormData.keyFeatures || [],
    thisCourseData: perType?.thisCourseData || cleanedFormData.thisCourseData || [],
    whoisthisFor: perType?.whoisthisFor || cleanedFormData.whoisthisFor || [],
    wherecaniWork: perType?.wherecaniWork || cleanedFormData.wherecaniWork || [],
    clinicalProcedures: perType?.clinicalProcedures || cleanedFormData.clinicalProcedures || [],
    nationalCertifications: perType?.nationalCertifications || cleanedFormData.nationalCertifications || { isallIncluded: false, items: [] },
    workPlaces: perType?.workPlaces || cleanedFormData.workPlaces || [],
    faqs: perType?.faqs || cleanedFormData.faqs || [],
    podcastData: perType?.podcastData || cleanedFormData.podcastData || [],
    programComparison: perType?.programComparison || cleanedFormData.programComparison || { showComparisonTable: false, comparisonCategories: [] },
    premiumDescription: perType?.premiumDescription || cleanedFormData.premiumDescription,
    premiumTagline: perType?.premiumTagline || cleanedFormData.premiumTagline,
    premiumRating: perType?.premiumRating || cleanedFormData.premiumRating,
    premiumReviewCount: perType?.premiumReviewCount || cleanedFormData.premiumReviewCount,
    premiumStudentCount: perType?.premiumStudentCount || cleanedFormData.premiumStudentCount,
    premiumViewCount: perType?.premiumViewCount || cleanedFormData.premiumViewCount,
    premiumLikeCount: perType?.premiumLikeCount || cleanedFormData.premiumLikeCount,
    premiumShareCount: perType?.premiumShareCount || cleanedFormData.premiumShareCount,
  });

  const baseData = {
    ...sharedData,
    categoryId: cleanedFormData.categoryId
  };

  // Transform payment options to match backend format
  const transformedPaymentOptions = transformPaymentOptionsForBackend(cleanedFormData.typeSpecificData?.paymentOptions || []);

  // Create schedule based on program type - removed unused variable

  // Create delivery mode mapping
  const DELIVERY_MODE_MAP: Record<string, string> = {
    'hybrid': 'Hybrid',
    'via-zoom': 'Via-Zoom',
    'on-campus': 'On-Campus',
    'online': 'Online'
  };

  // Build programTypeDataMap for all available types using typeSpecificDataByType when present
  const programTypeDataMap: Record<string, any> = {};
  const availableTypes: string[] = Array.isArray(cleanedFormData.availableProgramTypes)
    ? cleanedFormData.availableProgramTypes
    : [cleanedFormData.programType].filter(Boolean);

  const byType = cleanedFormData.typeSpecificDataByType || {};
  availableTypes.forEach((mode: string) => {
    // Always prefer the currently active typeSpecificData for the active mode
    const perType = (mode === cleanedFormData.programType)
      ? cleanedFormData.typeSpecificData
      : byType[mode] || undefined;
    
    // Build schedule based on program type - FIXED to match backend schema
    let perSchedule: any;
    if (perType?.programSchedule) {
      if (mode === 'hybrid') {
        // For hybrid programs, use the hybrid schedule structure
        perSchedule = {
          startMonth: perType.programSchedule?.hybrid?.startMonth || perType.programSchedule?.startMonth || [],
          startDate: perType.programSchedule?.hybrid?.startDate || perType.programSchedule?.startDate || [],
          clinicalLocations: perType.programSchedule?.hybrid?.clinicalLocations || perType.programSchedule?.clinicalLocations || [],
          practiceHours: perType.programSchedule?.hybrid?.practiceHours || perType.programSchedule?.practiceHours || 0,
          capacity: perType.programSchedule?.hybrid?.capacity || perType.programSchedule?.capacity || 0,
          facilityRequirements: perType.programSchedule?.hybrid?.facilityRequirements || perType.programSchedule?.facilityRequirements || []
        };
      } else if (mode === 'on-campus') {
        // For on-campus programs, use the on-campus schedule structure
        perSchedule = {
          batches: perType.programSchedule?.['on-campus']?.batches || perType.programSchedule?.batches || [],
          clinicalLocations: perType.programSchedule?.['on-campus']?.clinicalLocations || perType.programSchedule?.clinicalLocations || [],
          practiceHours: perType.programSchedule?.['on-campus']?.practiceHours || perType.programSchedule?.practiceHours || 0,
          capacity: perType.programSchedule?.['on-campus']?.capacity || perType.programSchedule?.capacity || 0,
          facilityRequirements: perType.programSchedule?.['on-campus']?.facilityRequirements || perType.programSchedule?.facilityRequirements || []
        };
      } else if (mode === 'via-zoom') {
        // For via-zoom programs, use the via-zoom schedule structure
        perSchedule = {
          batches: perType.programSchedule?.['via-zoom']?.batches || perType.programSchedule?.batches || [],
          clinicalLocations: perType.programSchedule?.['via-zoom']?.clinicalLocations || perType.programSchedule?.clinicalLocations || [],
          practiceHours: perType.programSchedule?.['via-zoom']?.practiceHours || perType.programSchedule?.practiceHours || 0,
          minParticipants: perType.programSchedule?.['via-zoom']?.minParticipants || perType.programSchedule?.minParticipants || 0,
          maxParticipants: perType.programSchedule?.['via-zoom']?.maxParticipants || perType.programSchedule?.maxParticipants || 0,
          facilityRequirements: perType.programSchedule?.['via-zoom']?.facilityRequirements || perType.programSchedule?.facilityRequirements || []
        };
      } else if (mode === 'online') {
        // For online programs, use the online schedule structure
        perSchedule = {
          startMonth: perType.programSchedule?.online?.startMonth || perType.programSchedule?.startMonth || [],
          startDate: perType.programSchedule?.online?.startDate || perType.programSchedule?.startDate || [],
          enrollmentPeriods: perType.programSchedule?.online?.enrollmentPeriods || perType.programSchedule?.enrollmentPeriods || []
        };
      }
    } else {
      // Construct empty schedule per type with proper structure
      if (mode === 'hybrid') {
        perSchedule = { 
          startMonth: [], 
          startDate: [], 
          clinicalLocations: [], 
          practiceHours: 0,
          capacity: 0,
          facilityRequirements: []
        };
      } else if (mode === 'on-campus') {
        perSchedule = { 
          batches: [], 
          clinicalLocations: [],
          practiceHours: 0,
          capacity: 0,
          facilityRequirements: []
        };
      } else if (mode === 'via-zoom') {
        perSchedule = { 
          batches: [], 
          clinicalLocations: [],
          practiceHours: 0,
          minParticipants: 0,
          maxParticipants: 0,
          facilityRequirements: []
        };
      } else if (mode === 'online') {
        perSchedule = {
          startMonth: [],
          startDate: [],
          enrollmentPeriods: []
        };
      }
    }

    const paymentOptions = cleanPaymentOptions(transformPaymentOptionsForBackend(perType?.paymentOptions || cleanedFormData.typeSpecificData?.paymentOptions || cleanedFormData.paymentOptions || []));

    // Validate and clean the schedule structure
    const validatedSchedule = validateScheduleStructure(mode, perSchedule);

    // Get type-specific keyFeatures and update Delivery value based on program type
    const typeSpecificKeyFeatures = perType?.keyFeatures || cleanedFormData.keyFeatures || [];
    const updatedKeyFeatures = typeSpecificKeyFeatures.map((feature: any) => {
      if (feature.name === "Delivery") {
        return {
          ...feature,
          value: DELIVERY_MODE_MAP[(mode as DeliveryMode)] || 'Hybrid'
        };
      }
      return feature;
    });

    // Get type-specific data for this program type
    const typeSpecificData = getTypeSpecificData(perType);

    // Use the normalized mode directly instead of converting to legacy format
    // The API expects the normalized format: "via-zoom", "on-campus", etc.
    programTypeDataMap[mode] = {
      ...sharedData, // Use shared data (same across all types)
      ...typeSpecificData, // Use type-specific data (different per type)
      programType: mode, // Use normalized mode
      delivery: DELIVERY_MODE_MAP[(mode as DeliveryMode)] || 'Hybrid',
      schedule: validatedSchedule,
      paymentOptions,
      categoryId: cleanedFormData.categoryId,
      keyFeatures: updatedKeyFeatures // Use type-specific keyFeatures with correct Delivery value
    };
  });

  // Return the complete API-compatible structure
  // Use normalized types directly - the API expects "via-zoom", "on-campus", etc.

  // Create main program schedule based on the active program type and validate it
  const mainProgramSchedule = validateScheduleStructure(
    cleanedFormData.programType, 
    programTypeDataMap[cleanedFormData.programType]?.schedule || {}
  );

  return {
    ...baseData,
    ...getTypeSpecificData(cleanedFormData.typeSpecificData), // Add type-specific data for main program
    // Persist the explicit list of available types so backend doesn't infer all
    // Send normalized types directly to API
    availableProgramTypes: (Array.isArray(cleanedFormData.availableProgramTypes)
      ? cleanedFormData.availableProgramTypes
      : [cleanedFormData.programType]
    ),
    // Hint backend which type is being updated in edit flows
    programType: cleanedFormData.programType,
    // Some backends accept a separate active field (dashed naming)
    active: cleanedFormData.programType,
    defaultProgramType: cleanedFormData.defaultProgramType || cleanedFormData.programType,
    programTypeDataMap,
    // Ensure typeSpecificData matches the active programType
    typeSpecificData: programTypeDataMap[cleanedFormData.programType],
    // Main program schedule - separate from programTypeDataMap
    schedule: mainProgramSchedule,
    // Also include top-level paymentOptions mirroring the active type for routing safeguards
    paymentOptions: cleanPaymentOptions((programTypeDataMap[cleanedFormData.programType]?.paymentOptions) || transformedPaymentOptions),
    // Enhanced module configuration
    payAsYouGo: cleanedFormData.payAsYouGo,
    modulePricingEnabled: cleanedFormData.modulePricingEnabled,
    moduleConfiguration: cleanedFormData.moduleConfiguration ? {
      ...cleanedFormData.moduleConfiguration,
      modules: cleanedFormData.moduleConfiguration.modules?.map((module: any, index: number) => ({
        moduleId: typeof module.moduleId === 'string' ? module.moduleId : `module-${index + 1}`,
        name: module.name || module.label || `Module ${index + 1}`,
        description: module.description || '',
        price: module.price || 0,
        isActive: module.isActive !== false,
        order: module.order || index,
        metadata: module.metadata || {}
      })) || []
    } : undefined,
    modulePricingMetadata: cleanedFormData.modulePricingMetadata
  };
}

/**
 * Validate required fields for API submission
 */




/**
 * Validate and clean schedule structure based on program type
 */
export function validateScheduleStructure(programType: string, schedule: any) {
  if (programType === 'on-campus' || programType === 'via-zoom') {
    // Must have batches array
    if (!schedule.batches || !Array.isArray(schedule.batches)) {
      throw new Error('On-campus and via-zoom programs must have batches array');
    }
    
    // Remove any nested program type data and return clean structure
    const cleanSchedule = {
      batches: schedule.batches,
      clinicalLocations: schedule.clinicalLocations || [],
      practiceHours: schedule.practiceHours || 0,
      capacity: schedule.capacity || 0,
      facilityRequirements: schedule.facilityRequirements || [],
      minParticipants: schedule.minParticipants || 0,
      maxParticipants: schedule.maxParticipants || 0
    };
    
    return cleanSchedule;
  } else if (programType === 'hybrid' || programType === 'online') {
    // Must have startMonth and startDate arrays with content
    if (!schedule.startMonth || !Array.isArray(schedule.startMonth) || schedule.startMonth.length === 0) {
      // Provide default values instead of throwing error
      schedule.startMonth = ["October", "November"];
    }
    
    if (!schedule.startDate || !Array.isArray(schedule.startDate) || schedule.startDate.length === 0) {
      // Provide default values instead of throwing error
      schedule.startDate = ["2025-10-01", "2025-11-01"];
    }
    
    return {
      startMonth: schedule.startMonth,
      startDate: schedule.startDate,
      clinicalLocations: schedule.clinicalLocations || [],
      practiceHours: schedule.practiceHours || 0,
      capacity: schedule.capacity || 0,
      facilityRequirements: schedule.facilityRequirements || []
    };
  }
  
  return schedule;
}

/**
 * Enhanced schedule validation for different program types
 * Validates required fields based on program type as per backend requirements
 */
export function validateScheduleForProgramType(programType: string, scheduleData: any): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!scheduleData || typeof scheduleData !== 'object') {
    errors.push('Schedule data is required');
    return { isValid: false, errors };
  }

  if (programType === 'hybrid') {
    const hybridSchedule = scheduleData.hybrid;
    if (!hybridSchedule) {
      errors.push('Hybrid program must have schedule configuration');
    } else {
      if (!Array.isArray(hybridSchedule.startMonth) || hybridSchedule.startMonth.length === 0) {
        errors.push('Hybrid program must have at least one start month');
      }
      if (!Array.isArray(hybridSchedule.startDate) || hybridSchedule.startDate.length === 0) {
        errors.push('Hybrid program must have at least one start date');
      }
    }
  } else if (programType === 'online') {
    const onlineSchedule = scheduleData.online;
    if (!onlineSchedule) {
      errors.push('Online program must have schedule configuration');
    } else {
      if (!onlineSchedule.startMonth?.length && !onlineSchedule.startDate?.length) {
        errors.push('Online program must have either start months or start dates');
      }
    }
  } else if (programType === 'on-campus') {
    const onCampusSchedule = scheduleData['on-campus'];
    if (!onCampusSchedule) {
      errors.push('On-campus program must have schedule configuration');
    } else {
      if (!Array.isArray(onCampusSchedule.batches) || onCampusSchedule.batches.length === 0) {
        errors.push('On-campus program must have at least one batch');
      }
    }
  } else if (programType === 'via-zoom') {
    const viaZoomSchedule = scheduleData['via-zoom'];
    if (!viaZoomSchedule) {
      errors.push('Via-zoom program must have schedule configuration');
    } else {
      if (!Array.isArray(viaZoomSchedule.batches) || viaZoomSchedule.batches.length === 0) {
        errors.push('Via-zoom program must have at least one batch');
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
}

/**
 * Clean payment options by removing invalid fields
 */
export function cleanPaymentOptions(paymentOptions: any[]) {
  return paymentOptions.map(option => {
    const cleanOption = { ...option };
    
    // Remove only truly invalid fields, preserve legitimate data
    delete cleanOption._id; // Only remove MongoDB _id, keep currency
    
    // Only add fallbacks if fields are completely missing (not empty strings)
    if (!cleanOption.paymentOptionName || cleanOption.paymentOptionName.trim() === '') {
      cleanOption.paymentOptionName = cleanOption.displayName || cleanOption.planType || '';
    }
    
    if (!cleanOption.youSave || cleanOption.youSave.trim() === '') {
      cleanOption.youSave = '';
    }
    
    if (!cleanOption.paymentTags || cleanOption.paymentTags.trim() === '') {
      cleanOption.paymentTags = '';
    }
    
    return cleanOption;
  });
}

/**
 * Convert legacy program type to new format
 */
export const convertLegacyProgramType = (legacyType: string): DeliveryMode => {
  return LEGACY_TO_NEW_PROGRAM_TYPE[legacyType] || legacyType as DeliveryMode;
};

/**
 * Convert new program type to legacy format (for backward compatibility)
 */
export const convertToLegacyProgramType = (newType: DeliveryMode): string => {
  return NEW_TO_LEGACY_PROGRAM_TYPE[newType] || newType;
};

/**
 * Format validation errors for display
 */
export const formatValidationErrors = (errors: string[]): string => {
  if (errors.length === 0) return "";
  
  if (errors.length === 1) {
    return errors[0];
  }
  
  if (errors.length <= 3) {
    return errors.join(", ");
  }
  
  return `${errors.slice(0, 3).join(", ")} and ${errors.length - 3} more error${errors.length - 3 > 1 ? 's' : ''}`;
};

/**
 * Clean validation error messages for better display
 */
export const cleanValidationMessage = (message: string): string => {
  // Remove common prefixes and make more user-friendly
  return message
    .replace(/^Path `/, '')
    .replace(/` is required\.$/, ' is required')
    .replace(/` must be a valid URL\.$/, ' must be a valid URL')
    .replace(/` must be at least \d+ characters long\.$/, ' must be longer')
    .replace(/` must be one of the following values: (.+)\.$/, ' must be one of: $1')
    .replace(/` must be a valid date\.$/, ' must be a valid date')
    .replace(/` must be a number\.$/, ' must be a number')
    .replace(/` must be a boolean\.$/, ' must be true or false');
};

/**
 * Group validation errors by section
 */
export const groupValidationErrors = (errors: Record<string, string>): Record<string, string[]> => {
  const grouped: Record<string, string[]> = {};
  
  Object.entries(errors).forEach(([field, message]) => {
    const section = field.split('.')[0];
    if (!grouped[section]) {
      grouped[section] = [];
    }
    grouped[section].push(`${field}: ${message}`);
  });
  
  return grouped;
};

// Module transformation utilities
export function transformToUnifiedModule(module: any): any {
  return {
    label: module.label || module.name || '',
    items: module.items || [],
    price: module.price || 0,
    isActive: module.isActive !== false,
    order: module.order || 0,
    metadata: module.metadata || {
      difficulty: 'BEGINNER',
      tags: [],
      duration: 0
    }
  };
}

export function transformFromUnifiedModule(unifiedModule: any): any {
  return {
    label: unifiedModule.label,
    items: unifiedModule.items || [],
    price: unifiedModule.price || 0,
    isActive: unifiedModule.isActive !== false,
    order: unifiedModule.order || 0,
    metadata: unifiedModule.metadata || {}
  };
}

// Transform enhanced modules to unified module structure
export const transformModulesToUnified = (modules: any[]): any[] => {
  return modules.map((module, index) => ({
    label: module.name || module.label || `Module ${index + 1}`,
    price: module.price || 0,
    isActive: module.isActive !== false,
    order: module.order || index
  }));
};

// Transform unified modules back to enhanced format
export const transformUnifiedToModules = (unifiedModules: any[]): any[] => {
  return unifiedModules.map((module, index) => ({
    moduleId: module.id || `module-${index}`,
    name: module.label,
    price: module.price || 0,
    isActive: module.isActive !== false,
    order: module.order || index
  }));
};

// Backend response handling
export function handleBackendResponse(response: any): any {
  if (response.data?.modules) {
    return {
      ...response,
      data: {
        ...response.data,
        modules: response.data.modules
      }
    };
  }
  return response;
}

/**
 * Transform payment options for backend API compatibility
 * Ensures youSave remains as strings (backend requirement)
 */
export const transformPaymentOptionsForBackend = (paymentOptions: any[]): any[] => {
  if (!Array.isArray(paymentOptions)) return [];
  
  return paymentOptions.map((option, index) => {
    // Preserve original paymentOptionName, only use displayName as fallback if completely missing
    const paymentOptionName = option.paymentOptionName || option.displayName || '';
    
    // Convert ID to number first
    const numericId = typeof option.id === 'string' ? parseInt(option.id) || (index + 1) : (option.id || index + 1);
    
    // Build the transformed option with paymentOptionName positioned early
    const { id: _, ...optionWithoutId } = option; // Remove id from spread
    const transformedOption = {
      // Core required fields first
      id: numericId,
      planType: option.planType?.replace(/-/g, '_') || option.planType,
      displayName: option.displayName || '',
      paymentOptionName: paymentOptionName, // Preserve original or use displayName fallback
      numberOfPayments: option.numberOfPayments || 1,
      amount: option.amount || 0,
      frequency: option.frequency === 'One Time' ? 'ONCE' : 
                 option.frequency === 'daily' || option.frequency === 'Daily' ? 'DAILY' :
                 option.frequency === 'every 2 weeks' ? 'BI_WEEKLY' :
                 option.frequency === 'every month' ? 'MONTHLY' : 
                 option.frequency,
      youPay: option.youPay || 0,
      youSave: typeof option.youSave === 'string' ? option.youSave : String(option.youSave || ''),
      paymentTags: Array.isArray(option.paymentTags) 
        ? option.paymentTags.join(', ') 
        : option.paymentTags,
      // Other fields
      squarePlanId: option.squarePlanId,
      slug: option.slug,
      cardTag: option.cardTag || { available: false },
      currency: option.currency || "USD", // Default to USD if completely missing
      // Preserve any additional fields (excluding id)
      ...optionWithoutId
    };
    
    // Don't force generic names - preserve original data integrity
    return transformedOption;
  });
};

